/**
 * 管理后台路由配置
 * 定义应用的路由结构和导航逻辑
 */

import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { ROUTE_NAMES, ROUTE_PATHS } from '@/constants/routes'
import { moduleLoader } from '@/core/module-loader'
import { getAllRoutes } from '@/utils/route-utils'

// 布局组件
const Layout = () => import('../layouts/index.vue')

// 基础路由配置
const constantRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/home'
  },

  {
    path: ROUTE_PATHS.LOGIN,
    name: ROUTE_NAMES.LOGIN,
    component: () => import('../modules/auth/pages/login.vue'),
    meta: {
      title: '登录',
      hidden: true,
      noAuth: true
    }
  },
  {
    path: '/403',
    name: 'error-403',
    component: () => import('../modules/error/pages/403.vue'),
    meta: {
      title: '访问被拒绝',
      hidden: true,
      noAuth: true
    }
  },
  {
    path: '/500',
    name: 'error-500',
    component: () => import('../modules/error/pages/500.vue'),
    meta: {
      title: '服务器错误',
      hidden: true,
      noAuth: true
    }
  },

  {
    path: '/redirect/:pathMatch(.*)*',
    name: 'Redirect',
    component: () => import('../pages/redirect.vue'),
    meta: {
      title: '重定向',
      hidden: true,
      noAuth: true
    }
  },

  // 工作流设计器路由（支持新标签页直接访问）
  {
    path: '/aimanagement/workflow-designer/:id?',
    name: 'WorkflowDesigner',
    component: () => import('../modules/aimanagement/pages/workflow-designer.vue'),
    meta: {
      title: '工作流设计器',
      hidden: true,
      noAuth: true,
      layout: 'blank',
      layoutConfig: {
        fullscreen: true,
        background: 'default',
        padding: '0'
      }
    }
  },
  {
    path: '/aimanagement/workflow-designer-v2/:id?',
    name: 'WorkflowDesignerV2',
    component: () => import('../modules/aimanagement/pages/workflow-designer-v2.vue'),
    meta: {
      title: '工作流设计器 V2',
      hidden: true,
      noAuth: true,
      layout: 'blank',
      layoutConfig: {
        fullscreen: true,
        background: 'default',
        padding: '0'
      }
    }
  },

  {
    path: '/home',
    name: 'Home',
    component: Layout,
    meta: {
      title: '首页',
      icon: 'HomeFilled',
      noAuth: true
    },
    children: [
      {
        path: '',
        name: 'HomeIndex',
        component: () => import('../modules/dashboard/pages/index.vue'),
        meta: {
          title: '首页',
          icon: 'HomeFilled',
          affix: true,
          hideInBreadcrumb: true
        }
      }
    ]
  },

  // 兜底路由必须静态声明：守卫依赖稳定的 CatchAll 名称拿到原始路径做"跳登录/重试/403/404"判定，
  // 若由模块动态注册（或带 matcher 级 redirect），未注册路径会被改写成白名单路径 /404 绕过守卫
  {
    path: '/:pathMatch(.*)*',
    name: 'CatchAll',
    component: () => import('../modules/error/pages/404.vue'),
    meta: {
      title: '页面不存在',
      hidden: true,
      noAuth: true
    }
  }

]

// 动态路由配置（需要权限验证）
// 这些路由将从模块配置中动态生成
const asyncRoutes: RouteRecordRaw[] = [
  // 动态路由将通过模块系统加载
]

// 初始化模块加载器
export const initModuleLoader = async () => {
  try {
    await moduleLoader.loadModules()

    // 加载模块配置系统中的路由
    const moduleRoutes = await getAllRoutes()
    addRoutes(moduleRoutes)
  } catch (error) {
    console.error('❌ 模块加载器初始化失败:', error)
  }
}

// 创建路由实例
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: constantRoutes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 留给应用启动流程在合适时机初始化模块路由

// 重置路由
export function resetRouter() {
  const newRouter = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: constantRoutes
  })

  // 替换路由实例的matcher
  ;(router as any).matcher = (newRouter as any).matcher
}

// 动态添加路由
export function addRoutes(routes: RouteRecordRaw[]) {
  routes.forEach(route => {
    router.addRoute(route)
  })
}

// 获取异步路由
export function getAsyncRoutes() {
  return asyncRoutes
}

// 路由错误处理
router.onError((error) => {
  console.error('路由错误:', error)

  // 可以在这里添加错误上报逻辑
  // errorReporter.report(error)
})

// 导出路由实例和相关配置
export { constantRoutes, asyncRoutes }
export default router
